// Pure helpers for ConceptEditor.vue. Extracted so the preferred-tracking logic
// (the critical correctness piece) is unit-testable without mounting the Vuetify
// component.

import { CHARACTERIZATION_AXES } from "@/constants/characterizationAxes";

// Characterization axis keys (the shared frontend source of truth; mirror of the backend
// CHARACTERIZATION_AXES). Drives buildTagScope/seedAxisScope, so a key missing here is
// silently stripped on save and not hydrated on load - hence the single shared list.
export const AXIS_KEYS = CHARACTERIZATION_AXES;

// Build the stored `tag_scope` object from the editor's three scope inputs, or null
// when ALL are empty. Trims/drops blanks and dedups; keeps only non-empty axes. Mirrors
// the backend normalizeTagScope (DCTL-2025): an axis-only scope (axisScope set, no
// tag/group scope) must NOT collapse to null.
export function buildTagScope({
	tagNames = [],
	tagGroupNames = [],
	axisScope = {},
} = {}) {
	const clean = (list) => [
		...new Set(
			(list || [])
				.map((name) => (typeof name === "string" ? name.trim() : ""))
				.filter(Boolean),
		),
	];
	const names = clean(tagNames);
	const groupNames = clean(tagGroupNames);
	const cleanedAxis = {};
	for (const axis of AXIS_KEYS) {
		const ids = clean(axisScope?.[axis]);
		if (ids.length) cleanedAxis[axis] = ids;
	}
	const hasAxis = Object.keys(cleanedAxis).length > 0;
	if (names.length === 0 && groupNames.length === 0 && !hasAxis) return null;
	const scope = {};
	if (names.length) scope.tagNames = names;
	if (groupNames.length) scope.tagGroupNames = groupNames;
	if (hasAxis) scope.axisScope = cleanedAxis;
	return scope;
}

// Hydrate the editor's per-axis axisScope selection from a stored `tag_scope.axisScope`
// (guards pre-5c.4 concepts that lack the key: each axis defaults to []).
export function seedAxisScope(axisScope) {
	const source = axisScope || {};
	const out = {};
	for (const axis of AXIS_KEYS) {
		out[axis] = Array.isArray(source[axis]) ? [...source[axis]] : [];
	}
	return out;
}

// Resolve the preferred synonym's CURRENT text from the row identified by a
// stable client uid. Tracking the preferred selection by uid - not by the synonym
// text, which is two-way bound and mutable - is what keeps it correct when the
// curator edits the preferred row's text in place (e.g. to fix a typo). Comparing
// by text would silently detach the star and, on submit, send a stale preferred
// that the backend rejects (edit) or persists as a phantom duplicate row (add).
export function resolvePreferredText(synonyms, preferredUid) {
	if (preferredUid === null || preferredUid === undefined) return "";
	const row = synonyms.find((s) => s.uid === preferredUid);
	return row && typeof row.synonym === "string" ? row.synonym.trim() : "";
}

// The cleaned synonym payload: non-empty, trimmed, with source/sourceType carried
// through. The resolved preferred text is always one of these (it comes from a
// row), so the backend's "preferred must be among the synonyms" check holds.
export function cleanSynonyms(synonyms) {
	return synonyms
		.filter((s) => s.synonym && s.synonym.trim())
		.map((s) => ({
			synonym: s.synonym.trim(),
			source: s.source ?? null,
			sourceType: s.sourceType ?? null,
		}));
}

// Find the first pair of synonyms that collide on the normalized fold, returning
// { duplicate, existing } surfaces (later-vs-first-seen) or null when all are
// distinct. Used to BLOCK a duplicate before saving in create/edit mode, where a
// collision always means the curator entered a value the concept already carries
// (existing rows are distinct-fold, so resending them can't collide) - instead of
// the backend silently folding it away. The add-from-EVS mode is deliberately
// excluded by the caller: EVS synonyms legitimately fold together and the backend
// aggregates their sources. The backend stays the canonicalFold authority; this
// trim+whitespace+lowercase fold is strictly weaker (a subset of canonicalFold),
// so it never flags a genuinely-distinct synonym - at worst a rare unicode variant
// slips through to the backend's existing dedup.
export function findDuplicateSynonym(synonyms) {
	const fold = (value) =>
		String(value ?? "")
			.replace(/\s+/g, " ")
			.trim()
			.toLowerCase();
	const seen = new Map(); // fold -> first-seen surface
	for (const s of synonyms) {
		const surface = (s.synonym ?? "").trim();
		if (!surface) continue;
		const key = fold(surface);
		const prior = seen.get(key);
		if (prior !== undefined) {
			return { duplicate: surface, existing: prior };
		}
		seen.set(key, surface);
	}
	return null;
}
